import React, { useMemo } from 'react';

import Stack from '@gogame/ui/Stack';
import {NoResults} from '@gogame/ui/NoResults';

import ListToolbar from '../ListToolbar';
import VirtualizedList from '../../VirtualizedList';

const EMPTY_ARR = [];
const Metrics = ({
  metrics = EMPTY_ARR,
  selected = EMPTY_ARR,
  onSelect,
  filtered,
  secondaryAction,
  formatData,
  getOptionMetricValue,
  getOptionLabel,
  getOptionHint,
  numberOfGroupMaxSelectedItems,
  ensureCustomMetricsGroup = true,
  isInitialExpanded,
  groupAction,
  grouped = false,
}) => {
  const isSelected = React.useCallback(
    (option) => {
      return selected.includes(getOptionMetricValue(option));
    },
    [selected]
  );

  const formattedMetrics = useMemo(
    () =>
      formatData(metrics, {
        ensureCustomMetricsGroup,
      }),
    [metrics]
  );

  const handleGroupMetricToggle = React.useCallback(
    (_, option, { selected: state }) => {
      if (onSelect) {
        const children = option.children;

        const groupValues = children.map((child) =>
          getOptionMetricValue(child)
        );

        const newSelection = [
          ...selected.filter((v) => !groupValues.includes(v)),
          ...(state
            ? []
            : numberOfGroupMaxSelectedItems
              ? groupValues.slice(0, numberOfGroupMaxSelectedItems)
              : groupValues),
        ];

        onSelect(newSelection);
      }
    },
    [selected, numberOfGroupMaxSelectedItems]
  );

  const handleItemMetricToggle = (_, option, { selected: state }) => {
    if (onSelect) {
      const metricV = getOptionMetricValue(option);

      onSelect(
        state
          ? selected.filter((selected) => metricV !== selected)
          : [...selected, metricV]
      );
    }
  };

  const isEmpty = !metrics.length;

  const isDisabled = React.useCallback(
    (option, row, params) => {
      const { parent, siblings } = params;

      if (numberOfGroupMaxSelectedItems && parent) {
        const selectedSiblings = siblings.filter((child) =>
          selected.includes(getOptionMetricValue(child))
        );

        return (
          selectedSiblings.length === numberOfGroupMaxSelectedItems &&
          !selectedSiblings.includes(row)
        );
      }

      return false;
    },
    [selected, metrics, numberOfGroupMaxSelectedItems, formattedMetrics]
  );

  const _isInitialExpandedRef = React.useRef(null);
  _isInitialExpandedRef.current = isInitialExpanded;

  const handleIsInitialExpanded = React.useCallback(
    (el) => {
      return _isInitialExpandedRef.current
        ? _isInitialExpandedRef.current(el)
        : filtered;
    },
    [filtered]
  );

  return (
    <Stack style={{ width: '400px' }} flex={1}>
      <ListToolbar title={`Available (${metrics.length})`} />
      {isEmpty ? (
        <NoResults inited={!!metrics} filtered={filtered} />
      ) : (
        <VirtualizedList
          data={formattedMetrics}
          secondaryAction={secondaryAction}
          grouped={grouped}
          isDisabled={isDisabled}
          isSelected={isSelected}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionMetricValue}
          getOptionSecondaryLabel={getOptionHint}
          onItemClick={handleItemMetricToggle}
          button
          checkbox
          groupCheckbox
          // groups
          onGroupChange={handleGroupMetricToggle}
          isInitialExpanded={handleIsInitialExpanded}
          groupActionComponent={groupAction}
        />
      )}
    </Stack>
  );
};

export default Metrics;
